import { useEffect } from "react";
import { SITE } from "@/data/site";

// Image de partage par défaut (1200x630) utilisée quand une page ne fournit
// pas la sienne — garantit qu'aucune page ne se retrouve sans og:image/twitter:image.
const DEFAULT_OG_IMAGE = `${SITE.siteUrl}/og-image.jpg`;

/**
 * SEO — gère title, meta description, canonical, og et JSON-LD par page.
 * Injecte / met à jour les balises dans <head> à chaque navigation.
 */
export default function SEO({ title, description, canonical, jsonLd, image, noindex = false }) {
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      if (title && title.length > 62) {
        console.warn(`[SEO] Title trop long (${title.length} car., max 62) sur ${canonical || "?"} : "${title}"`);
      }
      if (description && (description.length < 140 || description.length > 165)) {
        console.warn(`[SEO] Meta description hors plage 140-165 (${description.length} car.) sur ${canonical || "?"} : "${description}"`);
      }
    }

    if (title) document.title = title;

    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!content) {
        if (el) el.remove();
        return;
      }
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const ogImage = image || DEFAULT_OG_IMAGE;
    // 1200x630 n'est garanti que pour l'image de partage par défaut — une
    // image par page (blog, ville) a son propre ratio, donc pas de largeur/
    // hauteur annoncée dans ce cas (une valeur fausse est pire qu'absente).
    const ogImageIsDefault = !image;

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:width", ogImageIsDefault ? "1200" : null);
    setMeta("property", "og:image:height", ogImageIsDefault ? "630" : null);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    setMeta("name", "robots", noindex ? "noindex" : null);

    // canonical — retirée sur une page noindex (404) pour ne pas pointer
    // vers l'accueil, dont la canonical est codée en dur dans index.html.
    if (noindex) {
      document.querySelector('link[rel="canonical"]')?.remove();
    } else if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-jsonld="true"]');
    existingScripts.forEach((s) => s.remove());

    const items = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    items.forEach((obj) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoJsonld = "true";
      script.textContent = JSON.stringify(obj);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((s) => s.remove());
    };
  }, [title, description, canonical, image, jsonLd, noindex]);

  return null;
}
